import * as React from 'react';
import { Plus } from 'react-feather';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { H3 } from '../../typography';
import { Box, Button, Flex, Loader } from './index';

type TableProps = {
  data?: any[];
  manual?: boolean;
  onFetchData?: any;
  onPageChange?: any;
  columns?: any[];
  showPagination?: boolean;
  showPageSizeOptions?: boolean;
  minRows?: number | undefined;
  pageSizeOptions?: any[];
  padding?: number;
  pages?: number;
  heading?: string;
  subHeading?: string;
  hideActionButton?: boolean;
  hideActionButton2?: boolean;
  loading?: boolean;
  buttonText?: string;
  buttonBg?: string;
  button2Text?: string;
  buttonIcon?: React.ReactNode;
  button2Icon?: React.ReactNode;
  filters?: React.ReactChild;
  /** Extra buttons shown before the standard action buttons, in the same group. */
  extraActions?: React.ReactNode;
  hasAction?: boolean;
  defaultPageSize?: number;
  showHeader?: boolean;
  headingPb?: number;
  NoDataComponent?: React.ComponentType;
  onRowClick?: (rowInfo: any) => void;
  onButtonPress?: () => void;
  onButton2Press?: () => void;
  getTableProps?: any;
  getTbodyProps?: any;
  getTrProps?: any;
  getTdProps?: any;
};

type TableState = {
  data?: any[];
  pages?: any;
  loading?: boolean;
  fetching?: boolean;
};

const StyledTable = styled(ReactTable)`
  flex: 1;
  background: white;
  border-radius: var(--ib-radius-md, 8px);
  border: 1px solid ${themeGet('colors.border', '#e3e6e6')} !important;
  box-shadow: var(--ib-shadow-sm);
  /* react-table already scrolls .rt-table sideways; capping the width keeps wide tables doing
     that inside the card instead of pushing the whole page wider */
  max-width: 100%;
  /* TODO: try get rid of important tags here */
  & .rt-thead.-header {
    box-shadow: 0 0px 4px 0px rgba(50, 50, 93, 0.3) !important;
  }
  & .rt-thead .rt-th {
    padding: 15px !important;

    &:first-child {
      text-align: left;
    }
  }

  & .rt-td {
    justify-content: center;
    align-items: center;
    display: flex;
    white-space: normal !important;
    text-align: center !important;

    &:first-child {
      justify-content: flex-start;
      padding-left: 15px !important;
      text-align: left !important;
    }
  }

  & .rt-tr-group:hover {
    cursor: default;
  }

  /* action buttons in cells inherit the 150px minimum meant for page-level buttons, which made
     them spill out of narrow columns; let them shrink to the column instead */
  & .rt-td button {
    min-width: 0;
    max-width: 100%;
  }

  ${themeGet('media.belowLaptop', '@media screen and (max-width: 1023px)')} {
    & .-pagination {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100% !important;

      & .-previous {
        width: 100% !important;
      }
      & .-center {
        width: 100% !important;
      }
      & .-next {
        width: 100% !important;
      }
    }
  }
`;

const NoDataWrapper = styled(Box as any)`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0px 7px -1px rgba(50, 50, 93, 0.3);
`;

const NoData = () => (
  <NoDataWrapper p={4} bg="white" color="primary" borderRadius={4} position="absolute">
    <H3>No Rows Found</H3>
  </NoDataWrapper>
);

const ButtonWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;

  & > button {
    margin-left: 0;
  }
`;

// Heading, filters and actions share one row while there is room and wrap onto a new line when
// there isn't, rather than the buttons running off the edge of the card.
const HeaderRow = styled(Box as any)`
  flex-wrap: wrap;
  gap: 12px;
`;

// Sized to its content and not shrunk, so the heading keeps its natural width instead of wrapping
// into a narrow column. The h3 margin is dropped so the subheading sits directly under the title.
const HeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  margin-right: 24px;

  h3 {
    margin: 0;
  }
`;

// Deliberately the title's own colour at 60% rather than a second grey, so the two read as one
// block: same family and hue, dropped a step in size and lightened to sit under the heading.
const SubHeading = styled.div`
  font-family: ${themeGet('fontFamily')};
  font-size: 16px;
  font-weight: 300;
  letter-spacing: 0.2px;
  line-height: 1.4;
  color: ${themeGet('colors.transparentPrimary', 'rgba(61,72,74,0.6)')};
  padding-top: 2px;
`;

class Table extends React.Component<TableProps, TableState> {
  static defaultProps = {
    padding: 4,
    data: [],
    columns: [],
    manual: false,
    showPagination: true,
    showPageSizeOptions: true,
    pageSizeOptions: [5, 10, 20, 25, 50, 100],
    defaultPageSize: 10,
    heading: 'New Table',
    buttonBg: 'primary',
    buttonText: 'Add New',
    button2Text: 'Button 2',
    hideActionButton: false,
    hideActionButton2: true,
    showHeader: true,
    headingPb: 4,
    NoDataComponent: NoData,
  };

  _mounted = false;

  constructor(props: TableProps) {
    super(props);
    this.state = {
      pages: null,
      loading: true,
      fetching: false,
    };
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  // Paging fetches are silent by default because Apollo leaves `loading` false during fetchMore.
  // An onFetchData handler that returns its promise gets a spinner for the duration of the fetch;
  // handlers that return nothing behave exactly as before.
  _handleFetchData = (state: any, instance: any) => {
    const { onFetchData } = this.props;
    if (!onFetchData) {
      return;
    }

    const result = onFetchData(state, instance);

    if (result && typeof result.then === 'function') {
      this.setState({ fetching: true });
      const done = () => {
        if (this._mounted) {
          this.setState({ fetching: false });
        }
      };
      result.then(done, done);
    }
  };

  _handleRowClick(rowInfo: any) {
    const { onRowClick } = this.props;
    if (onRowClick) {
      onRowClick(rowInfo);
    }
    return null;
  }

  _renderData(data: any) {
    if (Array.isArray(data)) {
      return data;
    }

    if (data && data.edges) {
      return data.edges.map((edge: any) => edge.node);
    }

    return [];
  }

  render() {
    const {
      data,
      columns,
      manual,
      onFetchData,
      onPageChange,
      filters,
      extraActions,
      heading,
      subHeading,
      hideActionButton,
      hideActionButton2,
      onButtonPress,
      onButton2Press,
      pages,
      buttonText,
      buttonBg,
      button2Text,
      buttonIcon,
      button2Icon,
      defaultPageSize,
      pageSizeOptions,
      loading,
      showHeader,
      headingPb,
      padding,
      showPagination,
      showPageSizeOptions,
      minRows,
      getTableProps,
      getTbodyProps,
      getTrProps,
      getTdProps,
      NoDataComponent,
    } = this.props;

    return (
      <Box p={padding} width="100%" flex={1}>
        {showHeader && (
          <HeaderRow pb={headingPb} width="100%" flexDirection="row" alignItems="center" justifyContent="space-between">
            <Flex direction="row" size="1">
              {/*
                Keyed on the prop being passed at all, not on it having a value: callers fill the
                heading in asynchronously, and switching wrappers once it arrives would shift the
                title as the h3 margin reset comes and goes. Tables that never pass subHeading are
                undefined here and keep their original markup.
              */}
              {subHeading !== undefined ? (
                <HeadingBlock>
                  <H3>{heading}</H3>
                  {subHeading ? <SubHeading>{subHeading}</SubHeading> : null}
                </HeadingBlock>
              ) : (
                <H3>{heading}</H3>
              )}
              {filters && filters}
            </Flex>
            <ButtonWrap>
              {extraActions}
              {!hideActionButton2 && (
                <Button
                  bg="primary"
                  type="button"
                  color="white"
                  width="auto"
                  maxWidth="300px"
                  text={button2Text}
                  onClick={onButton2Press}
                  icon={button2Icon || <Plus size={20} color="white" />}
                />
              )}
              {!hideActionButton && (
                <Button
                  bg={buttonBg ? buttonBg : 'primary'}
                  type="button"
                  color="white"
                  width="auto"
                  maxWidth="300px"
                  text={buttonText}
                  onClick={onButtonPress}
                  icon={buttonIcon || <Plus size={20} color="white" />}
                />
              )}
            </ButtonWrap>
          </HeaderRow>
        )}
        <StyledTable
          getTrProps={(finalState: any, rowInfo?: any, column?: undefined, instance?: any) => {
            if (rowInfo) {
              return {
                onClick: () => {
                  this._handleRowClick(rowInfo);
                },
              };
            }
            return getTrProps ? getTrProps(finalState, rowInfo, column, instance) : {};
          }}
          getTableProps={getTableProps}
          getTbodyProps={getTbodyProps}
          getTdProps={getTdProps}
          data={this._renderData(data)}
          manual={manual}
          columns={columns}
          loading={Boolean(loading) || Boolean(this.state.fetching)}
          pages={pages}
          pageSizeOptions={pageSizeOptions}
          LoadingComponent={Loader}
          NoDataComponent={NoDataComponent}
          onFetchData={onFetchData ? this._handleFetchData : undefined}
          onPageChange={onPageChange}
          className="-striped -highlight"
          defaultPageSize={defaultPageSize}
          showPagination={showPagination}
          showPageSizeOptions={showPageSizeOptions}
          minRows={minRows}
        />
      </Box>
    );
  }
}

export default Table;
